import { ImageDataInfoClass } from 'interface/data/image/info';
import { Logger } from '../middlewares/sentry';
import { ImageService } from '../services';
import { Language } from 'common/languages';

export interface IImage {
  name: string;
  url: string;
  forceNew: boolean;
}

export class ImageClass {
  name: string = '';
  url: string = '';
  forceNew: boolean = false;
}

export function initializeImages<T>(
  ImageClass: new () => T,
): Record<Language, T> {
  return Object.fromEntries(
    Object.values(Language).map((lang) => [lang, new ImageClass()]),
  ) as Record<Language, T>;
}

export function resetImages(
  clientImages: Record<string, Record<string, IImage | IImage[]>>,
) {
  for (const imagesStructure of Object.values(clientImages)) {
    for (const image of Object.values(imagesStructure)) {
      if (Array.isArray(image)) {
        image.forEach((item) => {
          item.name = '';
          URL.revokeObjectURL(item.url);
          item.url = '';
          item.forceNew = true;
        });
      } else {
        image.name = '';
        URL.revokeObjectURL(image.url);
        image.url = '';
        image.forceNew = true;
      }
    }
  }
}

export async function getImage(
  clientImages: Record<string, Record<string, IImage | IImage[]>>,
  data: ImageDataInfoClass[],
  type: string,
  lang: string,
  index?: number,
): Promise<void> {
  const items = data.filter((item) => {
    if (!item.info.lang || item.info.lang === '') {
      item.info.lang = lang;
    }

    if (item.info?.purpose) {
      const itemId = item.info.id;
      if (index !== undefined && itemId) {
        return (
          item.info.purpose === type &&
          item.info.lang === lang &&
          index === parseInt(itemId, 10)
        );
      } else {
        return item.info.purpose === type && item.info.lang === lang;
      }
    }
    return false;
  });

  if (items.length === 0) {
    return;
  }

  const imageName = items.pop()?.name;
  if (!imageName) {
    return;
  }

  let clientImage =
    index !== undefined
      ? clientImages[lang]?.[type]?.[index]
      : clientImages[lang]?.[type];

  if (!clientImage) {
    if (!clientImages[lang]) {
      clientImages[lang] = {};
    }
    if (!clientImages[lang][type]) {
      clientImages[lang][type] = [];
    }
    if (Array.isArray(clientImages[lang][type])) {
      (clientImages[lang][type] as ImageClass[]).push(new ImageClass());
      clientImage = (clientImages[lang][type] as ImageClass[])[0];
    } else {
      clientImages[lang][type][index!] = new ImageClass();
      clientImage = clientImages[lang][type][index!];
    }
  }

  if (clientImage && clientImage.name !== imageName) {
    await downloadImage(clientImage, imageName);
  }
}

async function downloadImage(clientImage: IImage, imageName) {
  try {
    const fileData = await ImageService.getClientImageData(
      imageName,
      clientImage.forceNew,
    );
    const url = URL.createObjectURL(fileData);
    clientImage.name = imageName;
    clientImage.url = url;
    clientImage.forceNew = false;
  } catch (err) {
    Logger.error(err);
  }
}

export function deepClone(obj) {
  if (typeof structuredClone === 'function') {
    return structuredClone(obj);
  } else {
    return JSON.parse(JSON.stringify(obj));
  }
}
